// W-B.R16 · one editor, two tabs. Replaces the two-entry mess
// (open-manual-crop + patch-tool-button, two different layouts)
// Vania reported on 10 Oct 23:40.
//
// Shell:
//   slim top bar (h-12) · [back · back to details] [crop | patch]
//   [primary action pinned here · save crop OR apply patch]
//   below · poster pane (flex-1 min-h-0) + rail (260px lg / 96px <lg)
//
// Both tabs stay MOUNTED (css display toggles) so switching Crop →
// Patch → Crop never loses the user's work:
//   · Crop tab's live bbox is preserved
//   · Patch tab's drawn boxes + applied-banner are preserved
//
// Default tab: Crop when the row still needs a crop (no cover image or
// cover_source is empty / needs_recrop / skipped / errored); otherwise
// Patch.
//
// The editor hides review-filter-row (via its parent) and overlaps the
// whole right side of the admin, so fit zoom uses the whole height
// under the 52px top bar, which is what Vania asked for.
import React, { useEffect, useRef, useState } from "react";
import ManualCropEditor from "@/components/admin/ManualCropEditor";
import PatchToolPanel from "@/components/admin/PatchToolPanel";
import { coverSrc } from "@/lib/coverSrc";

const KILI = "#FFD60A";
const INK = "#1A1A30";

export default function PosterEditor({
  coverMissing = false,
  pendingId,
  driveFileId,
  initialBbox,
  coverUrl,
  cropBaseCoverUrl = "",
  originalCoverUrl,
  defaultTab = "crop",
  posterConfirmed = false,
  posterGate = null,
  posterCheck = null,
  coverFingerprint = "",
  availableDriveFiles = [],
  onClose,
  onCropSaved,
  onPatchApplied,
  onPatchRestored,
  onNoPoster,
  onPosterConfirmed,
  onPosterUnconfirmed,
  onReplaceUploaded,
  onReplaceDrivePicked,
}) {
  const [tab, setTab] = useState(() => {
    if (defaultTab === "patch") return "patch";
    if (defaultTab === "replace") return "replace";
    return "crop";
  });
  const [cropSaving, setCropSaving] = useState(false);
  const [patchBoxCount, setPatchBoxCount] = useState(0);
  // W-B.R22 · R22.1 · hard-lock Apply when clone-mode patches miss a
  // source rect.  Count comes from the patch panel ref and polls at
  // the same cadence as the box count.
  const [patchMissingSource, setPatchMissingSource] = useState(0);
  const [patchFillMode, setPatchFillMode] = useState("smart");
  const [patchApplying, setPatchApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [replaceUploading, setReplaceUploading] = useState(false);
  const [generateComingSoon, setGenerateComingSoon] = useState(false);
  const [enteredFingerprint, setEnteredFingerprint] = useState(coverFingerprint);
  const changedSinceEntered = coverFingerprint !== enteredFingerprint;
  const gateState = posterGate?.state || (posterConfirmed ? "confirmed" : "not_checked");
  const needsConfirm = gateState !== "confirmed" || changedSinceEntered;
  const cropRef = useRef(null);
  const patchRef = useRef(null);

  // Keep patch-side box count in sync so the top bar can show
  // "apply · N" and disable when 0.
  useEffect(() => {
    const id = setInterval(() => {
      if (patchRef.current && typeof patchRef.current.getBoxCount === "function") {
        const n = patchRef.current.getBoxCount();
        setPatchBoxCount((cur) => (cur === n ? cur : n));
      }
      // W-B.R22.1 · also read the clone-source-missing count + fill mode
      if (patchRef.current && typeof patchRef.current.getMissingSourceCount === "function") {
        const m = patchRef.current.getMissingSourceCount();
        setPatchMissingSource((cur) => (cur === m ? cur : m));
      }
      if (patchRef.current && typeof patchRef.current.getFillMode === "function") {
        const f = patchRef.current.getFillMode();
        setPatchFillMode((cur) => (cur === f ? cur : f));
      }
    }, 250);
    return () => clearInterval(id);
  }, []);

  const onPrimary = async () => {
    if (tab === "crop") {
      if (!cropRef.current) return;
      setCropSaving(true);
      try { await cropRef.current.save(); } finally { setCropSaving(false); }
    } else {
      if (!patchRef.current) return;
      setPatchApplying(true);
      try { await patchRef.current.apply(); } finally { setPatchApplying(false); }
    }
  };

  // W-B.R22.1 · in clone mode, Apply must be truly disabled while any
  // patch still lacks a source rect (preset boxes land without one;
  // the manual target→source flow only pushes a complete patch).
  const patchCloneBlocked = patchFillMode === "clone" && patchMissingSource > 0;
  const primaryLabel = tab === "crop"
    ? (cropSaving ? "saving…" : "save crop")
    : (patchApplying
        ? "applying…"
        : patchCloneBlocked
          ? `apply patch · ${patchMissingSource} need${patchMissingSource === 1 ? "s" : ""} source`
          : `apply patch${patchBoxCount > 0 ? ` · ${patchBoxCount}` : ""}`);

  const primaryDisabled = tab === "crop"
    ? cropSaving
    : (patchApplying || patchBoxCount === 0 || patchCloneBlocked);
  const primaryTitle = tab === "crop"
    ? ""
    : patchCloneBlocked
      ? "set a source box first"
      : (patchBoxCount === 0 ? "draw at least one box over an icon" : "");

  const confirmPoster = async () => {
    if (!onPosterConfirmed) return;
    setConfirming(true);
    try { await onPosterConfirmed(); setEnteredFingerprint(coverFingerprint); } finally { setConfirming(false); }
  };
  const unconfirmPoster = async () => {
    if (!onPosterUnconfirmed) return;
    setConfirming(true);
    try { await onPosterUnconfirmed(); } finally { setConfirming(false); }
  };

  return (
    <div data-testid="poster-editor-root"
         className="flex h-full min-h-0 w-full flex-col"
         style={{ background: "#FBF4E9" }}>
      {/* Top bar · pinned · never scrolls.
          W-B.R22.2 · wrap below lg (1024px) so the primary action
          never clips on phones + tablets.  Row 1 keeps back + tabs,
          the right-hand group (chip + confirm + apply) drops to a
          full-width second row at <lg. */}
      <div data-testid="editor-top-bar"
           className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2 lg:h-12 lg:flex-nowrap lg:py-0"
           style={{ borderColor: "#E4D7C0", background: "#FFFFFF" }}>
        <button type="button" data-testid="edit-back" onClick={onClose}
                className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold"
                style={{ borderColor: INK, color: INK, background: "#FFFFFF" }}>
          <span className="whitespace-nowrap">back to details</span>
        </button>
        <div className="flex shrink-0 items-stretch rounded-md border overflow-hidden" style={{ borderColor: INK }}>
          <button type="button" data-testid="edit-tab-crop" onClick={() => setTab("crop")}
                  className="whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
                  style={tab === "crop"
                    ? { background: INK, color: "#FFFFFF" }
                    : { background: "#FFFFFF", color: INK }}>
            <span className="whitespace-nowrap">crop</span>
          </button>
          <button type="button" data-testid="edit-tab-patch" onClick={() => setTab("patch")}
                  className="whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-l"
                  style={{
                    ...(tab === "patch"
                      ? { background: INK, color: "#FFFFFF" }
                      : { background: "#FFFFFF", color: INK }),
                    borderColor: INK,
                  }}>
            <span className="whitespace-nowrap">patch</span>
          </button>
          <button type="button" data-testid="edit-tab-replace" onClick={() => setTab("replace")}
                  className="whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-l"
                  style={{
                    ...(tab === "replace"
                      ? { background: INK, color: "#FFFFFF" }
                      : { background: "#FFFFFF", color: INK }),
                    borderColor: INK,
                  }}>
            <span className="whitespace-nowrap">replace</span>
          </button>
        </div>
        <div data-testid="editor-top-bar-actions"
             className="ml-0 flex w-full shrink-0 flex-wrap items-center justify-end gap-2 lg:ml-auto lg:w-auto lg:flex-nowrap">
          {/* R17 (amended) · 4-state chip · never hidden */}
          <span data-testid="poster-status-chip"
                data-status={gateState}
                className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
                style={gateState === "confirmed"
                  ? { borderColor: "#1F7A3A", background: "#D7F4D9", color: "#1F7A3A" }
                  : gateState === "no_poster"
                    ? { borderColor: "#9A2222", background: "#F8DADA", color: "#7A1A1A" }
                    : { borderColor: "#B2550D", background: "#FCE9CF", color: "#7A3C09" }}>
            {gateState === "confirmed" ? "poster ok"
              : gateState === "no_poster" ? "needs a poster"
              : gateState === "needs_work" ? "poster needs work"
              : "poster not checked"}
          </span>
          {needsConfirm ? (
            <button type="button" data-testid="poster-confirm" onClick={confirmPoster}
                    disabled={confirming || coverMissing || gateState === "no_poster" || gateState === "needs_work"}
                    title={coverMissing ? "poster missing" : gateState === "no_poster" ? "Replace this poster first" : gateState === "needs_work" ? "Fix the poster first · see reasons" : ""}
                    className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                    style={{ borderColor: INK, background: KILI, color: INK }}>
              <span className="whitespace-nowrap">{confirming ? "…" : "poster looks good"}</span>
            </button>
          ) : (
            <button type="button" data-testid="poster-unconfirm" onClick={unconfirmPoster} disabled={confirming}
                    className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    style={{ borderColor: "#8A8171", background: "#FFFFFF", color: "#5C5548" }}>
              <span className="whitespace-nowrap">undo confirm</span>
            </button>
          )}
          <button type="button" data-testid="edit-primary-action"
                  onClick={onPrimary} disabled={primaryDisabled || tab === "replace"}
                  aria-disabled={primaryDisabled || tab === "replace"}
                  title={primaryTitle}
                  className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                  style={{ borderColor: INK, background: needsConfirm ? "#FFFFFF" : KILI, color: INK }}>
            <span className="whitespace-nowrap">{tab === "replace" ? "pick a replacement →" : primaryLabel}</span>
          </button>
        </div>
      </div>

      {/* R17 · nag line · covers all non-confirmed states */}
      {needsConfirm && (
        <div data-testid="poster-change-notice"
             className="flex h-6 shrink-0 items-center justify-center border-b px-3 text-[11px]"
             style={{ borderColor: "#E4D7C0", background: "#FDF3DC", color: "#7A3C09" }}>
          <span className="whitespace-nowrap">
            {gateState === "no_poster"
              ? "no poster yet · use Replace to upload, pick a screenshot, or generate one"
              : gateState === "needs_work"
                ? `poster needs work · ${(posterGate?.reasons || []).join(" · ") || "see the editor"}`
                : changedSinceEntered
                  ? "poster changed, please check it again"
                  : "poster not checked yet · look at it and confirm"}
          </span>
        </div>
      )}

      {/* Body · one child per tab · both mounted · state preserved. */}
      <div data-testid="editor-body" className="flex min-h-0 flex-1 overflow-hidden">
        <div data-testid="editor-tab-crop-pane"
             style={{ display: tab === "crop" ? "flex" : "none" }}
             className="min-h-0 flex-1 flex-col overflow-hidden p-2">
          <ManualCropEditor ref={cropRef}
                            key={cropBaseCoverUrl || driveFileId}
                            pendingId={pendingId}
                            driveFileId={driveFileId}
                            uploadUrl={cropBaseCoverUrl}
                            fallbackUrl={driveFileId ? "" : coverSrc(coverUrl)}
                            initialBbox={initialBbox}
                            embedded
                            onSaved={onCropSaved}
                            onNoPoster={onNoPoster} />
        </div>
        <div data-testid="editor-tab-patch-pane"
             style={{ display: tab === "patch" ? "flex" : "none" }}
             className="min-h-0 flex-1 overflow-hidden">
          <PatchToolPanel ref={patchRef}
                          pendingId={pendingId}
                          driveFileId={driveFileId}
                          coverUrl={coverUrl}
                          originalCoverUrl={originalCoverUrl}
                          embedded
                          onApplied={onPatchApplied}
                          onRestored={onPatchRestored} />
        </div>
        <div data-testid="editor-tab-replace-pane"
             style={{ display: tab === "replace" ? "flex" : "none" }}
             className="min-h-0 flex-1 flex-col overflow-y-auto p-4" >
          <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
            <h2 className="text-lg font-bold" style={{ color: INK }}>replace this poster</h2>
            <p className="text-[12px]" style={{ color: "#5C5548" }}>
              {gateState === "no_poster"
                ? "this event has no poster. pick one of the three options below."
                : "replace the current poster if the crop or patch cannot be fixed."}
            </p>
            {coverUrl && (
              <div data-testid="replace-current-poster" className="rounded-md border p-2" style={{ borderColor: "#E4D7C0", background: "#FFFFFF" }}>
                <p className="mb-2 text-[11px] uppercase tracking-wider" style={{ color: "#8A8171" }}>current poster</p>
                {coverMissing ? (
                  <p data-testid="replace-current-poster-missing" className="text-xs text-amber-800">poster missing · upload a new one below</p>
                ) : (
                <img data-testid="replace-current-poster-img" key={coverUrl} alt="current poster"
                     src={coverSrc(coverUrl)}
                     onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("p"), { className: "text-xs text-amber-800", textContent: "poster missing · upload a new one below" })); }}
                     className="mx-auto block max-h-80 w-auto rounded" />
                )}
              </div>
            )}
            {/* 1 · upload */}
            <label data-testid="replace-upload"
                   className="inline-flex cursor-pointer flex-col items-start gap-1 rounded-md border-2 px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-neutral-50"
                   style={{ borderColor: INK, background: "#FFFFFF", color: INK }}>
              <span className="whitespace-nowrap">{replaceUploading ? "uploading…" : "upload your own poster"}</span>
              <span data-testid="replace-upload-hint" className="whitespace-normal text-[10px] font-normal" style={{ color: "#8A8171" }}>jpg or png · will go through the gate</span>
              <input type="file" accept="image/*" className="hidden" disabled={replaceUploading}
                     onChange={async (e) => {
                       const file = e.target.files?.[0];
                       if (!file || !onReplaceUploaded) return;
                       setReplaceUploading(true);
                       try { await onReplaceUploaded(file); } finally {
                         setReplaceUploading(false);
                         e.target.value = "";
                       }
                     }} />
            </label>
            {/* 2 · pick another screenshot */}
            {availableDriveFiles.length > 1 && (
              <div data-testid="replace-pick-drive" className="rounded-md border p-3" style={{ borderColor: "#E4D7C0", background: "#FFFFFF" }}>
                <p className="mb-2 text-[11px] uppercase tracking-wider" style={{ color: "#8A8171" }}>
                  pick a different screenshot from this event's drive files
                </p>
                <div className="flex flex-wrap gap-2">
                  {availableDriveFiles.map((did) => (
                    <button key={did} type="button"
                            data-testid={`replace-drive-${did.slice(0, 8)}`}
                            onClick={() => onReplaceDrivePicked?.(did)}
                            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-semibold hover:bg-neutral-50"
                            style={{ borderColor: did === driveFileId ? INK : "#8A8171", background: did === driveFileId ? "#F1E7D6" : "#FFFFFF", color: INK }}>
                      <span className="whitespace-nowrap">{did === driveFileId ? "current · " : ""}{did.slice(0, 10)}…</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* 3 · generate */}
            <div className="rounded-md border p-3" style={{ borderColor: "#E4D7C0", background: "#FFFFFF" }}>
              <button type="button" data-testid="poster-generate" onClick={() => setGenerateComingSoon(true)}
                      className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 px-4 py-3 text-xs font-bold uppercase tracking-wider"
                      style={{ borderColor: INK, background: KILI, color: INK }}>
                <span className="whitespace-nowrap">generate a poster with watu wako style</span>
              </button>
              {generateComingSoon && (
                <div data-testid="poster-generate-coming-soon"
                     className="mt-3 rounded-md border px-3 py-2 text-[12px]"
                     style={{ borderColor: "#B2550D", background: "#FCE9CF", color: "#7A3C09" }}>
                  <p className="font-semibold">coming soon · event stays blocked</p>
                  <p className="mt-1">to ship this: pass event title, start date, venue, iko tag and the watu wako brand style pulled from the Drive masters folder.  this is a separate wave.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
